import { arenaBoundsFor } from './balance'
import { drawKnightDustCoded } from './knight_dust_art'
import { KNIGHT_CROSS_HOP_AT_MS, KNIGHT_LEAP_AIR_MS, KNIGHT_LEAP_SCRAPE_MS } from './knight_leap_slash'
import { KNIGHT_CHARGE_SCRAPE_MS, KNIGHT_CHARGE_SPEED } from './knight_wall_charge'
import type { Boss, FxEvent } from './types'
import { projectBossSpriteSourcePoint, type BossSpritePlacement } from './sprites'

export interface Grit { x: number; y: number; floorY: number; vx: number; vy: number; born: number; life: number; size: number; a: number; tone: number }
export interface Pebble { x: number; y: number; vx: number; vy: number; born: number; life: number; r: number; spin: number; rot: number; verts: number[] }
export interface Puff { fx: Extract<FxEvent, { kind: 'knight_lunge_dust' }>; born: number }
export const GRIT_G = 1300
export const PEBBLE_G = 1700
export const BUTT_SRC_X = 1488
export const BUTT_SRC_Y = 893

/** Actual drawn butt, including the renderer's area scale, offsets, squash and facing. */
export function knightScrapeButtTip(placement: BossSpritePlacement): { x: number; y: number } | null {
  return projectBossSpriteSourcePoint(placement, [BUTT_SRC_X, BUTT_SRC_Y])
}

export class KnightFloorScrape {
  grits: Grit[] = []
  pebbles: Pebble[] = []
  puffs: Puff[] = []
  private lastStreakMs = -1
  private lastPebbleMs = -1
  private lastImpactId = ''
  private lastEmissionMs = -1
  private lastNow = -1
  private lastRoom = -1
  constructor(private readonly rng: () => number = Math.random) {}
  private rand(a: number, b: number): number { return a + this.rng() * (b - a) }
  clear(): void {
    this.grits = []; this.pebbles = []; this.puffs = []
    this.lastStreakMs = -1; this.lastPebbleMs = -1; this.lastImpactId = ''
    this.lastNow = -1; this.lastRoom = -1; this.lastEmissionMs = -1
  }
  emitStreak(x: number, y: number, back: number, now: number, power: number): void {
    const ang = this.rand(0.12, 0.8)
    const speed = this.rand(420, 760) * (0.55 + 0.45 * power)
    const n = Math.round(this.rand(16, 28) * (0.6 + 0.4 * power))
    for (let i = 0; i < n; i++) {
      const k = Math.pow(this.rng(), 0.7)
      const v = speed * (0.45 + 0.6 * k)
      const a = ang + this.rand(-0.05, 0.05)
      this.grits.push({ x: x + this.rand(-3, 3), y: y - this.rand(0, 3), floorY: y, vx: back * Math.cos(a) * v, vy: -Math.sin(a) * v,
        born: now, life: this.rand(260, 520), size: this.rand(0.7, 1.9) * (1.25 - 0.5 * k), a: this.rand(0.45, 0.95), tone: this.rng() })
    }
  }
  emitPebble(x: number, y: number, back: number, now: number, power: number): void {
    const ang = this.rand(0.35, 1.05)
    const v = this.rand(260, 520) * (0.6 + 0.4 * power)
    const nv = Math.round(this.rand(5, 7))
    const verts: number[] = []
    for (let i = 0; i < nv; i++) verts.push(this.rand(0.62, 1))
    this.pebbles.push({ x: x + this.rand(-4, 4), y: y - 3, vx: back * Math.cos(ang) * v, vy: -Math.sin(ang) * v, born: now,
      life: this.rand(650, 900), r: this.rand(2.2, 4.8), spin: this.rand(-14, 14), rot: this.rand(0, 6.3), verts })
  }
  emitImpact(x: number, y: number, back: number, now: number): void {
    for (let i = 0; i < 6; i++) this.emitStreak(x, y, back, now, 1)
    for (let i = 0; i < 3; i++) this.emitPebble(x, y, back, now, 1)
    this.puffs.push({ born: now, fx: { kind: 'knight_lunge_dust', variant: 'kick', x, y, t: now / 1000, dir: -back, amount: 0.55 } })
  }
  emitScrape(x: number, y: number, back: number, now: number, power: number): void {
    if (power <= 0.05) return
    if (now - this.lastStreakMs > 34 / (0.4 + 0.6 * power)) { this.emitStreak(x, y, back, now, power); this.lastStreakMs = now }
    if (now - this.lastPebbleMs > 110 / (0.3 + 0.7 * power)) { this.emitPebble(x, y, back, now, power); this.lastPebbleMs = now }
  }
  sync(previous: Boss, boss: Boss, now: number, placement?: BossSpritePlacement | null): void {
    if (boss.id !== 'vigil-knight' || boss.hp <= 0) { this.clear(); return }
    if ((this.lastNow >= 0 && now < this.lastNow) || (this.lastRoom >= 0 && boss.executesTaken !== this.lastRoom)) this.clear()
    this.lastNow = now
    this.lastRoom = boss.executesTaken
    const id = boss.currentAttack?.id
    if (id !== 'lanceCross' && id !== 'knightWallCharge') {
      // The lab can replay the same attack without incrementing attackCount.
      this.lastImpactId = ''
      return
    }
    const s = boss.state === 'active' ? boss.stateElapsed * 1000
      : boss.state === 'recovery' ? boss.currentAttack!.activeMs + boss.stateElapsed * 1000 : -1
    const scrapeFrom = id === 'lanceCross' ? KNIGHT_CROSS_HOP_AT_MS + KNIGHT_LEAP_AIR_MS : boss.currentAttack!.activeMs
    const scrapeAge = id === 'lanceCross' ? s - scrapeFrom : boss.state === 'recovery' ? boss.stateElapsed * 1000 : -1
    if (scrapeAge < 0 || scrapeAge >= (id === 'lanceCross' ? KNIGHT_LEAP_SCRAPE_MS : KNIGHT_CHARGE_SCRAPE_MS)) return
    // Simulation calls maintain reset boundaries; render calls supply this frame's exact draw placement.
    if (!placement || placement.imageKey !== 'scrape_stop' || now === this.lastEmissionMs) return
    const tip = knightScrapeButtTip(placement)
    if (!tip) return
    this.lastEmissionMs = now
    const facing: 1 | -1 = boss.facing === 'right' ? 1 : -1
    const back = -facing
    const { x, y } = tip
    const castId = `${boss.attackCount}:${id}`
    if (this.lastImpactId !== castId) { this.emitImpact(x, y, back, now); this.lastImpactId = castId }
    const u = Math.min(1, scrapeAge / (id === 'lanceCross' ? KNIGHT_LEAP_SCRAPE_MS : KNIGHT_CHARGE_SCRAPE_MS))
    const power = id === 'lanceCross' ? 1 - u : (KNIGHT_CHARGE_SPEED * (1 - u)) / KNIGHT_CHARGE_SPEED
    this.emitScrape(x, y, back, now, power)
    void previous
  }
  draw(ctx: CanvasRenderingContext2D, now: number, bossId: string): void {
    const walls = arenaBoundsFor(bossId)
    this.grits = this.grits.filter(g => now - g.born < g.life)
    this.pebbles = this.pebbles.filter(p => now - p.born < p.life)
    this.puffs = this.puffs.filter(p => now - p.born < 520)
    for (const p of this.puffs) { ctx.save(); drawKnightDustCoded(ctx, p.fx, (now - p.born) / 1000); ctx.restore() }
    ctx.save()
    for (const g of this.grits) {
      const t = (now - g.born) / 1000
      const drag = (1 - Math.exp(-2.2 * t)) / 2.2
      const x = g.x + g.vx * drag
      const y = Math.min(g.floorY - 0.5, g.y + g.vy * drag + 0.5 * GRIT_G * t * t)
      if (x < walls.leftWall || x > walls.rightWall) continue
      const k = (now - g.born) / g.life
      const alpha = g.a * (1 - k) * (1 - 0.35 * k)
      const s = g.size * (1 - 0.4 * k)
      const c = Math.round(188 + 50 * g.tone)
      ctx.fillStyle = `rgba(${c - 6}, ${c - 3}, ${c + 6}, ${alpha})`
      ctx.fillRect(x - s / 2, y - s / 2, s, s)
    }
    ctx.restore()
    for (const p of this.pebbles) {
      const age = (now - p.born) / 1000
      const floor = p.y + 3 - p.r * 0.8
      const tHit = (-p.vy + Math.sqrt(p.vy * p.vy + 2 * PEBBLE_G * Math.max(0, floor - p.y))) / PEBBLE_G
      let x: number, y: number, rot: number
      if (age < tHit) {
        x = p.x + p.vx * age; y = p.y + p.vy * age + 0.5 * PEBBLE_G * age * age; rot = p.rot + p.spin * age
      } else {
        const bx = p.x + p.vx * tHit
        const bvy = -0.25 * (p.vy + PEBBLE_G * tHit)
        const bvx = p.vx * 0.3
        const tUp = (-2 * bvy) / PEBBLE_G
        const tt = Math.min(age - tHit, tUp)
        x = bx + bvx * tt; y = floor + bvy * tt + 0.5 * PEBBLE_G * tt * tt
        rot = p.rot + p.spin * (tHit + 0.4 * tt)
      }
      const k = (now - p.born) / p.life
      x = Math.max(walls.leftWall + p.r, Math.min(walls.rightWall - p.r, x))
      ctx.save(); ctx.translate(x, y); ctx.rotate(rot)
      ctx.globalAlpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3
      ctx.beginPath()
      p.verts.forEach((m, i) => {
        const a = (i / p.verts.length) * Math.PI * 2
        const px = Math.cos(a) * p.r * m, py = Math.sin(a) * p.r * m * 0.8
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py)
      })
      ctx.closePath(); ctx.fillStyle = '#5d6070'; ctx.fill(); ctx.clip()
      ctx.fillStyle = '#b4b8c6'; ctx.fillRect(-p.r, -p.r, p.r * 2, p.r * 0.9)
      ctx.fillStyle = '#2f313b'; ctx.fillRect(-p.r * 0.2, p.r * 0.25, p.r * 1.4, p.r)
      ctx.restore()
    }
  }
}

export const knightFloorScrape = new KnightFloorScrape()
